import { type ReactNode } from "react"
import { Link, useLocation } from "react-router"
import { ArrowLeft, User01 } from "@untitledui/icons"
import { motion, AnimatePresence } from "motion/react"
import { useAuth } from "@/lib/auth/auth-context"
import { cx } from "@/lib/utils/cx"

// SettingsShell — full-page takeover. The user enters Settings as a
// focused workspace with no dashboard nav competing. Same energy as
// onboarding (rounded-3xl primary card on a textured backdrop, soft
// glow, dot grid).
//
// Layout:
//   Topbar: [← Back to app]   ────  [eTX Impa𝗫t]   ────   [Account]
//   Body  : [ Big rounded-3xl card with sidebar + content inside ]
//
// Both sidebar and content live inside the same outer card, separated
// by a thin border. That's the "two surfaces, one product" pattern
// onboarding uses (StepLayout's form-column + hero-column inside a
// single card).

const ONBOARDING_SPRING = {
  type: "spring",
  stiffness: 260,
  damping: 30,
  mass: 0.9,
} as const

interface SettingsShellProps {
  sidebar?: ReactNode
  children: ReactNode
}

export function SettingsShell({ sidebar, children }: SettingsShellProps) {
  const location = useLocation()
  const { user } = useAuth()
  const isOnAccount = location.pathname === "/settings/account"

  const accountInitial = (
    user?.firstName?.[0] ??
    user?.email?.[0] ??
    "?"
  ).toUpperCase()
  const accountName = user?.firstName
    ? `${user.firstName}${user.lastName ? " " + user.lastName : ""}`
    : (user?.email ?? "")

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-zinc-50 to-white">
      <div aria-hidden className="pointer-events-none fixed inset-0">
        <div className="absolute -top-32 right-[-200px] size-[680px] rounded-full bg-emerald-200/25 blur-3xl" />
        <div className="absolute top-1/2 -left-40 size-[440px] rounded-full bg-zinc-100/60 blur-3xl" />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div className="relative">
        <header className="sticky top-0 z-20 backdrop-blur-md bg-white/60">
          <div className="max-w-[1320px] mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-1.5 h-9 -ml-1 pl-1.5 pr-3 rounded-lg text-[13px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/70 transition-all duration-150"
            >
              <ArrowLeft className="size-3.5" aria-hidden />
              Back to app
            </Link>

            <div className="flex-1 flex items-center justify-center">
              <Link to="/dashboard" className="inline-flex items-center gap-2.5">
                <div className="size-9 rounded-xl bg-zinc-900 grid place-items-center shadow-md shadow-zinc-900/15">
                  <span
                    className="text-white font-black text-[12px]"
                    style={{ fontFamily: "var(--font-syne)" }}
                  >
                    eT<span className="text-emerald-400">X</span>
                  </span>
                </div>
                <span
                  className="hidden sm:inline text-[14px] font-bold text-zinc-900 tracking-tight"
                  style={{ fontFamily: "var(--font-syne)" }}
                >
                  Impa<span className="text-emerald-500">X</span>t
                </span>
              </Link>
            </div>

            <Link
              to="/settings/account"
              aria-current={isOnAccount ? "page" : undefined}
              aria-label="Your account"
              className={cx(
                "inline-flex items-center gap-2 h-10 pl-1 pr-3 rounded-xl transition-all duration-150",
                isOnAccount
                  ? "bg-zinc-900 text-white shadow-md shadow-zinc-900/15"
                  : "border border-zinc-200/70 bg-white/70 hover:border-zinc-300 hover:bg-white",
              )}
            >
              <div
                className={cx(
                  "size-8 rounded-lg grid place-items-center text-[11px] font-bold",
                  isOnAccount ? "bg-white/15 text-white" : "bg-zinc-900 text-white",
                )}
              >
                {accountInitial}
              </div>
              <span
                className={cx(
                  "hidden sm:inline text-[12.5px] font-semibold truncate max-w-[140px]",
                  isOnAccount ? "text-white" : "text-zinc-700",
                )}
              >
                {accountName || "Your account"}
              </span>
              <User01
                className={cx(
                  "sm:hidden size-4",
                  isOnAccount ? "text-white" : "text-zinc-500",
                )}
                aria-hidden
              />
            </Link>
          </div>
        </header>

        <div className="max-w-[1320px] mx-auto px-4 md:px-8 pb-10 md:pb-14 pt-2 md:pt-4">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={sidebar ? "with-sidebar" : "fullwidth"}
              initial={{ opacity: 0, y: 18, scale: 0.985 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ ...ONBOARDING_SPRING, opacity: { duration: 0.25 } }}
              className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden"
            >
              {sidebar ? (
                <div className="grid lg:grid-cols-[300px_minmax(0,1fr)]">
                  <div className="bg-gradient-to-br from-zinc-50/60 via-white to-emerald-50/30 border-b lg:border-b-0 lg:border-r border-zinc-100 p-5 md:p-6">
                    {sidebar}
                  </div>
                  <div className="p-7 md:p-10 lg:p-12 min-w-0">{children}</div>
                </div>
              ) : (
                <div className="p-7 md:p-10 lg:p-12">{children}</div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}
